import type { Configuration } from '@azure/msal-browser';
import { LogLevel, PublicClientApplication } from '@azure/msal-browser';
import { Capacitor } from '@capacitor/core';

/**
 * Inside the native iPad shell, MSAL's login redirect to login.microsoftonline.com
 * is intercepted at the native layer and re-presented in an ASWebAuthenticationSession
 * sheet — the system-browser context Apple requires for WebAuthn platform
 * authenticators (passkeys) — see ios/App/App/SceneDelegate.swift. The sheet can
 * only return control to the app through the `htsauth://` custom scheme, so the
 * redirect lands on /auth-bridge.html (an SPA-platform redirect URI — Entra only
 * permits cross-origin token redemption for SPA redirect URIs, AADSTS9002326),
 * which forwards Entra's fragment response to that scheme. The shell closes the
 * loop by reloading the app page with the response in the URL hash, which MSAL's
 * handleRedirectPromise parses on page load. Browsers/PWAs keep the https redirect.
 */
const nativeAuthRedirectUri = 'https://survey.hts.pro/auth-bridge.html';
const isNativeShell = Capacitor.isNativePlatform();


/**
 * Configuration object to be passed to MSAL instance on creation.
 * For a full list of MSAL.js configuration parameters, visit:
 * https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-browser/docs/configuration.md
 */
export const msalConfig: Configuration = {
  auth: {
    clientId: import.meta.env.VITE_AZURE_CLIENT_ID || '',
    authority: (() => {
      const tenantId = import.meta.env.VITE_AZURE_TENANT_ID;
      if (!tenantId) {
        throw new Error('FATAL: VITE_AZURE_TENANT_ID is not configured. Authentication cannot proceed without a tenant ID. Using "common" is not permitted.');
      }
      return `https://login.microsoftonline.com/${tenantId}`;
    })(),
    redirectUri: isNativeShell ? nativeAuthRedirectUri : import.meta.env.VITE_REDIRECT_URI || window.location.origin,
    postLogoutRedirectUri: isNativeShell ? nativeAuthRedirectUri : import.meta.env.VITE_POST_LOGOUT_REDIRECT_URI || window.location.origin,
  },
  cache: {
    // localStorage (not sessionStorage): the native iPad shell's WKWebView
    // starts a fresh sessionStorage on every app open, which forced a manual
    // Microsoft sign-in click each time despite the valid 15-day session
    // cookie. localStorage restores the MSAL account instantly and
    // acquireTokenSilent refreshes from the cached refresh token — the app
    // opens straight to content. Tradeoff: tokens become readable by XSS;
    // accepted because the httpOnly ss_session cookie (not these tokens) is
    // the API trust boundary, and MSAL tokens only serve direct
    // Graph calls. Sign-out clears this cache (MSAL logout).
    cacheLocation: 'localStorage',
  },
  system: {
    // Inside the native shell the login redirect never unloads the page —
    // the native layer cancels the navigation and re-presents it in the
    // system auth sheet (passkeys), then replays the response as a hash
    // reload (see ios/App/App/SceneDelegate.swift). MSAL's default 30s
    // redirect watchdog fires timed_out mid-sheet and wipes the interaction
    // cache (state + PKCE verifier) that the returning code needs, so give
    // the sheet room to finish. Browsers unload on redirect and never hit it.
    ...(isNativeShell ? { redirectNavigationTimeout: 21600000 } : {}),
    loggerOptions: {
      loggerCallback: (level: LogLevel, message: string, containsPii: boolean) => {
        if (containsPii) {
          return;
        }
        switch (level) {
          case LogLevel.Error:
            console.error(message);
            return;
          case LogLevel.Info:
            console.info(message);
            return;
          case LogLevel.Verbose:
            console.debug(message);
            return;
          case LogLevel.Warning:
            console.warn(message);
            return;
          default:
            return;
        }
      },
      logLevel: import.meta.env.VITE_ENVIRONMENT === 'development' ? LogLevel.Verbose : LogLevel.Error,
    },
  },
};

/**
 * Login request scopes -- minimal permissions for initial authentication.
 * Directory/mail scopes (graphScopes) are requested incrementally when first
 * needed via AuthService.getAccessToken.
 */
export const loginRequest = {
  scopes: ['User.Read'],
};

/**
 * Graph API scopes requested incrementally (not at login): directory display
 * names and P2P notification mail. The SharePoint-era Sites/Files scopes were
 * removed with the #239 cutover (2026-10-19).
 */
export const graphScopes = {
  user: ['User.Read', 'User.ReadBasic.All'],
  mail: ['Mail.Send'],
};

/**
 * API scopes for the backend Express API (api/src/server.ts).
 * Uses the GUID-based App Identifier to request a token for the API.
 * (Azure AD requires GUID format when an app requests a token for itself)
 */
export const apiScopes = {
  // Request a token with the app's client ID (GUID) as the audience
  default: [`${import.meta.env.VITE_AZURE_CLIENT_ID}/.default`],
};

/**
 * Initialize the MSAL instance
 */
/**
 * Expected tenant ID for post-login validation.
 * Tokens from other tenants will be rejected.
 */
export const EXPECTED_TENANT_ID = import.meta.env.VITE_AZURE_TENANT_ID || '';

/**
 * Initialize the MSAL instance
 */
export const msalInstance = new PublicClientApplication(msalConfig);
